import { useEffect, useState } from 'react';
import { X, Type, Layout } from 'lucide-react';
import { useAppStore } from '@/stores/useAppStore';
import { DEFAULT_SUBTITLE_STYLE, DEFAULT_SUBTITLE_SETTINGS } from '@/lib/constants';
import { loadSubtitleFont } from '@/lib/fontLoader';
import { FontSettings } from './subtitle/FontSettings';
import { ColorSettings } from './subtitle/ColorSettings';

interface SubtitleSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export function SubtitleSettingsModal({ open, onClose }: SubtitleSettingsModalProps) {
  const {
    subtitleSettings,
    updateSubtitleSettings,
    updateSubtitleDefaultStyle,
  } = useAppStore();

  const [fontLoaded, setFontLoaded] = useState(false);

  const { defaultStyle } = subtitleSettings;

  // フォント変更時にロード
  useEffect(() => {
    if (!open) return;

    // 非同期処理として実行（リントエラー回避）
    Promise.resolve().then(() => setFontLoaded(false));

    loadSubtitleFont(defaultStyle.fontFamily).then(() => {
      setFontLoaded(true);
    }).catch(() => {
      setFontLoaded(true);
    });
  }, [open, defaultStyle.fontFamily]);

  if (!open) return null;

  // プレビュー用のスタイル計算
  const previewFontSize = defaultStyle.fontSize * 0.4; // プレビュー用にスケール
  const previewStrokeWidth = defaultStyle.strokeWidth * 0.4;
  const previewStyle: React.CSSProperties = {
    fontFamily: `"${defaultStyle.fontFamily}", sans-serif`,
    fontSize: `${previewFontSize}px`,
    fontWeight: defaultStyle.fontWeight,
    color: defaultStyle.textColor,
    WebkitTextStroke: defaultStyle.strokeWidth > 0
      ? `${previewStrokeWidth}px ${defaultStyle.strokeColor}`
      : undefined,
    paintOrder: 'stroke fill',
    letterSpacing: `${subtitleSettings.letterSpacing}px`,
    lineHeight: subtitleSettings.lineHeight,
  };

  const previewBgStyle = {
    backgroundColor: defaultStyle.backgroundColor,
    opacity: defaultStyle.backgroundOpacity / 100,
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-[calc(100%-2rem)] md:w-[900px] max-w-[95vw] max-h-[calc(100dvh-2rem)] md:max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header shrink-0">
          <h2 className="flex items-center gap-2">
            <Type size={20} className="text-coral-500" />
            テロップ設定（デフォルト）
          </h2>
          <button onClick={onClose} className="btn-icon" aria-label="閉じる">
            <X size={20} />
          </button>
        </div>

        {/* Body - Side by Side Layout */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
          {/* Left: Preview (Fixed) */}
          <div className="w-full md:w-[340px] shrink-0 p-4 md:p-6 bg-warm-50 border-b md:border-b-0 md:border-r border-warm-200 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <Type size={16} className="text-warm-600" />
              <h3 className="font-semibold text-warm-800 text-sm">プレビュー</h3>
              {!fontLoaded && (
                <span className="text-xs text-warm-400">読み込み中...</span>
              )}
            </div>
            <div className="relative rounded-lg overflow-hidden h-32 md:h-auto md:flex-1 md:min-h-[200px]" style={{ backgroundColor: '#CCCCCC' }}>
              <div
                className="absolute bottom-0 left-0 right-0 flex justify-center"
                style={{ paddingBottom: `${Math.min(subtitleSettings.positionFromBottom / 10, 20)}%` }}
              >
                <div className="relative px-3 py-1.5">
                  <div className="absolute inset-0 rounded" style={previewBgStyle} />
                  <span
                    key={`${defaultStyle.fontFamily}-${fontLoaded}`}
                    className="relative transition-opacity duration-200 whitespace-nowrap"
                    style={{ ...previewStyle, opacity: fontLoaded ? 1 : 0.5 }}
                  >
                    サンプルテロップ
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs text-warm-400 mt-3 text-center">
              設定変更は即座に反映されます
            </p>
          </div>

          {/* Right: Settings (Scrollable) */}
          <div className="flex-1 overflow-y-auto min-h-0 p-4 md:p-6 space-y-6">
            {/* Font Settings */}
            <FontSettings style={defaultStyle} onUpdate={updateSubtitleDefaultStyle} />

            <div className="divider" />

            {/* Color Settings */}
            <ColorSettings style={defaultStyle} onUpdate={updateSubtitleDefaultStyle} />

            <div className="divider" />

            {/* Layout Settings */}
            <section>
              <div className="settings-section-header">
                <div className="settings-icon-box settings-icon-box-teal">
                  <Layout size={14} className="text-teal-600" />
                </div>
                <h3 className="settings-section-title">レイアウト設定（全体共通）</h3>
              </div>
              <div className="space-y-3 pl-9">
                <div className="settings-field-row">
                  <label className="settings-label settings-label-fixed-wide">下からの位置</label>
                  <input
                    type="range"
                    min="20"
                    max="200"
                    step="5"
                    value={subtitleSettings.positionFromBottom}
                    onChange={(e) => updateSubtitleSettings({ positionFromBottom: parseInt(e.target.value) })}
                    className="range-slider range-slider-flex"
                  />
                  <span className="settings-value settings-value-wide">{subtitleSettings.positionFromBottom}px</span>
                </div>

                <div className="settings-field-row">
                  <label className="settings-label settings-label-fixed-wide">左右の余白</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={subtitleSettings.horizontalPadding}
                    onChange={(e) => updateSubtitleSettings({ horizontalPadding: parseInt(e.target.value) })}
                    className="range-slider range-slider-flex"
                  />
                  <span className="settings-value settings-value-wide">{subtitleSettings.horizontalPadding}px</span>
                </div>

                <div className="settings-field-row">
                  <label className="settings-label settings-label-fixed-wide">行間</label>
                  <input
                    type="range"
                    min="1"
                    max="2"
                    step="0.1"
                    value={subtitleSettings.lineHeight}
                    onChange={(e) => updateSubtitleSettings({ lineHeight: parseFloat(e.target.value) })}
                    className="range-slider range-slider-flex"
                  />
                  <span className="settings-value settings-value-wide">{subtitleSettings.lineHeight}</span>
                </div>

                <div className="settings-field-row">
                  <label className="settings-label settings-label-fixed-wide">文字間隔</label>
                  <input
                    type="range"
                    min="-2"
                    max="10"
                    step="0.5"
                    value={subtitleSettings.letterSpacing}
                    onChange={(e) => updateSubtitleSettings({ letterSpacing: parseFloat(e.target.value) })}
                    className="range-slider range-slider-flex"
                  />
                  <span className="settings-value settings-value-wide">{subtitleSettings.letterSpacing}px</span>
                </div>
              </div>
            </section>

            <div className="divider" />

            {/* Reset Button */}
            <div className="flex justify-center">
              <button
                onClick={() => {
                  updateSubtitleDefaultStyle(DEFAULT_SUBTITLE_STYLE);
                  updateSubtitleSettings({
                    positionFromBottom: DEFAULT_SUBTITLE_SETTINGS.positionFromBottom,
                    horizontalPadding: DEFAULT_SUBTITLE_SETTINGS.horizontalPadding,
                    lineHeight: DEFAULT_SUBTITLE_SETTINGS.lineHeight,
                    letterSpacing: DEFAULT_SUBTITLE_SETTINGS.letterSpacing,
                  });
                }}
                className="btn-ghost text-warm-500 text-sm"
              >
                デフォルトに戻す
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer shrink-0">
          <button onClick={onClose} className="btn-primary w-full">
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}
